/*
 * Campaign Optimizer campaign inventory.
 *
 * Optimizer-scoped ON PURPOSE. The canonical resolver treats governed media
 * campaigns as enrichment for every other consumer, which meant the Optimizer's
 * dropdown inventory was defined solely by ad-platform (live Meta) and imported
 * reporting identities. An advertiser whose only campaign is a governed approved
 * plan with no platform identity therefore saw "No campaigns found" while the
 * very same page rendered that campaign's plan, flight and spend — the
 * contradictory state observed for advertiser 16 / media campaign 44.
 *
 * Campaign Optimizer optimizes against a governed campaign, so the governed
 * campaign is what it must offer. Campaign Analysis and Sherloc Reports keep the
 * platform-driven inventory; making governed campaigns option-producing across
 * the product is a deliberate cross-product decision, not a side effect of this
 * fix, so it is opted into here rather than switched on in the resolver.
 *
 * Eligibility and "in flight" are NOT redefined here — both are imported from
 * the canonical pacing/plan module so there is exactly one definition of an
 * approved campaign and one definition of a live flight.
 */

import {
  isInternalReportingExcludedCampaign,
  resolveCanonicalCampaigns,
} from "./canonicalCampaignIdentity";
import { extractFlightRange, isApprovedPlanCampaign } from "./optimusPacing";

// A governed campaign is eligible for the Optimizer selector when it is an
// approved plan and is not an internal supervised-flight authorization artifact.
// DRAFT and UNDER_REVIEW rows are not optimizable and never appear.
export const isOptimizerEligibleGovernedCampaign = (campaign) => {
  if (!campaign || typeof campaign !== "object") return false;
  if (isInternalReportingExcludedCampaign(campaign)) return false;
  return isApprovedPlanCampaign(campaign);
};

export const buildOptimusCampaignOptions = ({
  liveCampaigns = [],
  importedReportingCampaigns = [],
  mediaCampaigns = [],
  reportingContext = null,
} = {}) =>
  resolveCanonicalCampaigns({
    liveCampaigns,
    importedReportingCampaigns,
    mediaCampaigns,
    reportingContext,
    governedCampaignOptionFilter: isOptimizerEligibleGovernedCampaign,
  });

const toTime = (value) => {
  if (!value) return null;
  const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00Z`).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const optionFlightRange = (option = {}) => {
  const direct = {
    start: option.flightStartDate || null,
    end: option.flightEndDate || null,
  };
  if (direct.start || direct.end) return direct;
  const canonical = extractFlightRange(option) || {};
  return { start: canonical.date_start || null, end: canonical.date_end || null };
};

export const isOptionInFlight = (option = {}, nowMs = Date.now()) => {
  const { start, end } = optionFlightRange(option);
  const startMs = toTime(start);
  const endMs = toTime(end);
  if (startMs === null || endMs === null) return false;
  // End date is inclusive: a campaign is in flight through the whole end day.
  return nowMs >= startMs && nowMs <= endMs + 24 * 60 * 60 * 1000 - 1;
};

const flightStartMs = (option = {}) => toTime(optionFlightRange(option).start);
const flightEndMs = (option = {}) => toTime(optionFlightRange(option).end);

// The tie-break of last resort. Campaign ids are stable, server-assigned and
// unique, which is exactly what "deterministic" needs — unlike array position,
// which is whatever the API happened to order by, or the name, which two
// campaigns of the same advertiser can share a prefix of.
const stableIdRank = (option = {}) => {
  const raw = String(option?.id ?? option?.mediaCampaignId ?? "").trim();
  const numeric = Number.parseInt(raw, 10);
  return { numeric: Number.isFinite(numeric) ? numeric : Number.MAX_SAFE_INTEGER, raw };
};

const byStableId = (a, b) => {
  const left = stableIdRank(a);
  const right = stableIdRank(b);
  if (left.numeric !== right.numeric) return left.numeric - right.numeric;
  return left.raw.localeCompare(right.raw);
};

/*
 * Default campaign selection.
 *
 * THE RULE, stated once so it is a product decision rather than an accident of
 * whatever the API returned first:
 *
 *   1. A campaign currently in flight beats one that is not. Optimus optimizes
 *      delivery, and only a live flight has delivery to optimize.
 *   2. Among campaigns in flight, the one whose approved flight STARTED FIRST
 *      wins. It is the further-along campaign and it has the most observed
 *      delivery behind it, so it is the one an operator opening the Optimizer
 *      is most likely to be acting on. For Never Stop Chasing that is campaign
 *      44 (Aug 10-23) over Never Stop Chasing Incremental Markets (Aug 21-30).
 *   3. Same start: the flight that ENDS first wins — it is the one with the
 *      least remaining time to act.
 *   4. Still tied: the lower stable campaign id wins.
 *
 * With nothing in flight the same ladder runs on recency instead: latest start,
 * then latest end, then lowest stable id.
 *
 * `campaign` is null ONLY when the advertiser genuinely has no eligible
 * campaign. Returning null for an ambiguous advertiser is what left Campaign
 * Optimizer sitting on no campaign at all for an advertiser with two perfectly
 * good ones — an unmade decision presented as a safe one. The rule above always
 * decides, and the selector names the campaign it decided on.
 */
export const resolveOptimusDefaultCampaign = (options = [], nowMs = Date.now()) => {
  const list = (Array.isArray(options) ? options : []).filter(
    (option) => option && typeof option === "object"
  );

  if (!list.length) return { campaign: null, reason: "no_campaigns" };
  if (list.length === 1) return { campaign: list[0], reason: "only_campaign" };

  const inFlight = list.filter((option) => isOptionInFlight(option, nowMs));
  if (inFlight.length === 1) return { campaign: inFlight[0], reason: "in_flight" };
  if (inFlight.length > 1) {
    const ranked = [...inFlight].sort((a, b) => {
      const startDelta = (flightStartMs(a) ?? 0) - (flightStartMs(b) ?? 0);
      if (startDelta !== 0) return startDelta;
      const endDelta = (flightEndMs(a) ?? 0) - (flightEndMs(b) ?? 0);
      if (endDelta !== 0) return endDelta;
      return byStableId(a, b);
    });
    return { campaign: ranked[0], reason: "in_flight_earliest_start" };
  }

  const ranked = [...list].sort((a, b) => {
    const startDelta = (flightStartMs(b) ?? -Infinity) - (flightStartMs(a) ?? -Infinity);
    if (Number.isFinite(startDelta) && startDelta !== 0) return startDelta;
    const endDelta = (flightEndMs(b) ?? -Infinity) - (flightEndMs(a) ?? -Infinity);
    if (Number.isFinite(endDelta) && endDelta !== 0) return endDelta;
    return byStableId(a, b);
  });

  const topStart = flightStartMs(ranked[0]);
  if (topStart === null) return { campaign: ranked[0], reason: "undated_lowest_id" };
  if (flightStartMs(ranked[1]) === topStart) {
    return { campaign: ranked[0], reason: "most_recent_tie_broken_by_id" };
  }
  return { campaign: ranked[0], reason: "most_recent" };
};

/*
 * Which campaign the page should address, given everything that can name one.
 *
 * Precedence, and the reason for it:
 *
 *   1. An explicit campaign in the current route or page state — the operator
 *      (or the link they followed) said which campaign, so nothing may override
 *      it. It is honoured only if it is actually one of THIS advertiser's
 *      eligible campaigns.
 *   2. The deterministic default above.
 *
 * A requested campaign that is not in the inventory is DISCARDED, not carried:
 * a campaign id left over from another advertiser, another session, or a
 * browser that restored a stale form control must never address a campaign this
 * advertiser does not have, and must never silently fall through to a
 * same-named campaign.
 */
export const resolveOptimusSelectedCampaign = ({
  options = [],
  requestedCampaignId = null,
  nowMs = Date.now(),
} = {}) => {
  const list = (Array.isArray(options) ? options : []).filter(
    (option) => option && typeof option === "object"
  );

  const requested = String(requestedCampaignId ?? "").trim();
  if (requested && requested !== "auto") {
    const match = list.find((option) => String(option.id ?? "").trim() === requested);
    if (match) return { campaign: match, reason: "explicit_selection", discarded: null };
    return {
      ...resolveOptimusDefaultCampaign(list, nowMs),
      discarded: requested,
    };
  }

  return { ...resolveOptimusDefaultCampaign(list, nowMs), discarded: null };
};

/*
 * The selector's label for one campaign option.
 *
 * A campaign is named by its canonical name and qualified by its governed
 * status, and nothing else — the row must read as the campaign it addresses,
 * not as a mode or a scope. Status is title-cased because this renders in a
 * native <select>, which no stylesheet can case-correct: "(approved)" is the
 * raw column value leaking into the UI.
 */
export const formatCampaignOptionLabel = (campaign = {}) => {
  const name = String(campaign?.name || campaign?.displayName || "").trim();
  if (!name) return "";
  const status = String(campaign?.status || "").trim();
  if (!status) return name;
  const titled = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  return `${name} (${titled})`;
};
